import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'
import type { CustomerFormState } from './CustomerFormState'

import React, { useCallback, useEffect } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { useFetchSingleCustomer } from '@@partner:graphql/query/allCustomers/singleCustomer.gql'
import { useMutateSaveCustomer } from '@@partner:graphql/mutation/customers/saveCustomer.gql'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../helpers/notification'
import {
  makeCustomerFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './CustomerFormState'

import { Form, Toggle } from 'rsuite'
import { SaveButton } from '@components/common/form/Buttons'
import CustomerPhone from '../order/form/CustomerPhone'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

export interface CustomerFormProps extends CrudFormProps {
  customerId?: GQL_ID
}

export default function CustomerForm ({
  customerId,
  onSaved
}: CustomerFormProps) {
  const { formState, setFormState, setFormRef } =
    useValidatableForm<CustomerFormState>(makeCustomerFormState)

  const {
    data,
    loading: loadingCustomer,
    error: errorLoadingCustomer
  } = useFetchSingleCustomer({
    id: customerId
  })

  const [ saveCustomer, { loading: loadingSaveCustomer } ] =
    useMutateSaveCustomer()

  useEffect(() => {
    if (!loadingCustomer && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.customer))
      )
    }
  }, [ data, loadingCustomer, setFormState ])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )
  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveCustomer({
          variables: {
            newData: mapStateToApiVariable(formState),
            upsert: formState.id ? false : true
          }
        })
          .then(({ data }) => {
            notifyOk('Покупатель сохранён')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.customerInfo)
            )

            onSaved?.(data?.customerInfo.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveCustomer, formState, setFormState, onSaved ]
  )

  if (errorLoadingCustomer) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить покупателя #${customerId} для редактирования`}
        error={errorLoadingCustomer}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      layout="horizontal"
    >
      {customerId ? (
        <h3 className="u-form-heading">Пользователь №{customerId}</h3>
      ) : (
        <h3 className="u-form-heading">Добавить пользователя</h3>
      )}

      <Form.Group>
        <Form.ControlLabel>Имя</Form.ControlLabel>
        <Form.Control name="first_name" placeholder="Имя" />
      </Form.Group>

      <Form.Group>
        <Form.ControlLabel>Фамилия</Form.ControlLabel>
        <Form.Control name="last_name" placeholder="Фамилия" />
      </Form.Group>

      <Form.Group>
        <CustomerPhone name="phone" label="Телефон клиента" />
      </Form.Group>

      <Form.Group>
        <Form.ControlLabel>Email</Form.ControlLabel>
        <Form.Control name="receipt_email" placeholder="Email" />
      </Form.Group>

      <Form.Group>
        <Form.ControlLabel>Статус</Form.ControlLabel>

        <Form.Control
          accepter={Toggle}
          size="lg"
          name="status"
          checkedChildren="АКТИВЕН"
          unCheckedChildren="ЗАБЛОКИРОВАН"
          checked={Boolean(formState.status)}
          value={formState.status ? true : false}
          onChange={(value) =>
            setFormState((state) => ({
              ...state,
              status: value === true ? 1 : 0
            }))
          }
        />
      </Form.Group>

      <SaveButton
        loading={loadingCustomer || loadingSaveCustomer}
        disabled={loadingCustomer || loadingSaveCustomer}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
